import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, signal, ViewChild, Renderer2, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ListsService } from '../../services/lists.service';
import { CommonModule } from '@angular/common';

import { LockToggleComponent } from '../lock-toggle/lock-toggle.component';
import { FieldErrorTimeoutService } from '../../services/field-error-timeout.service';

@Component({
  selector: 'app-locations',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    LockToggleComponent
  ],
  templateUrl: './locations.component.html',
  styleUrls: ['./locations.component.css']
})
export class LocationsComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  locations: any[] = [];
  filteredLocations: any = signal([]);
  form: FormGroup;
  isValid = false;
  private isUpdating = false;

  @Input() disabled = false;
  @Input() resetTimer = false;
  @Input() defaultValue: any = null;
  @Input() label = '¿Dónde vives?';
  @Input() timeoutSeconds = 8;

  @Input() showBlocker = false;
  @Input() enabled = true;
  private lockTouched = false;

  @Output() valueState = new EventEmitter<{ value: number | null, isValid: boolean, isDirty: boolean }>();
  @ViewChild('location') location!: ElementRef;
  @ViewChild('subtitleLabel') subtitleLabel!: ElementRef;
  @ViewChild('inbox') inbox!: ElementRef;

  offTimeOut = signal(false);
  hasWritten = signal(false);
  private errorDebounceTimer: any;
  private countdownTimer: any;
  private viewInitialized = false;

  filterTimeout: any;
  selectedLocation: any = null;
  isLoading = false;
  private originalValue: any = null;
  private userEditing = false;
  private appliedLocationId: any = undefined;
  private locationRequest = 0;

  constructor(
    private listService: ListsService,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    private fieldErrorTimeout: FieldErrorTimeoutService
  ) {
    this.form = new FormGroup({
      location: new FormControl('', [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.startCountdown();

    this.listService.getUbication("")
      .subscribe({
        next: (response: any) => {
          this.locations = response.ubications;
        },
      });

    if (!this.enabled || this.disabled) {
      this.form.get('location')!.disable();
    }

    this.form.valueChanges.subscribe(() => {
      if (this.isUpdating) return;
      if (this.enabled && this.userEditing) {
        this.isValid = false;
        this.hasWritten.set(true);
        this.resetErrorDebounce();
      }
    });
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.applyInitialStyles();

    if (this.defaultValue) {
      this.originalValue = this.defaultValue;
      this.valueState.emit({ value: this.defaultValue, isValid: true, isDirty: false });
      this.cdr.detectChanges();
    }
  }

  ngOnChanges(): void {
    if (this.resetTimer) {
      this.clearCountdown();
      this.offTimeOut.set(false);
      this.startCountdown();
    }

    this.applyPreloadLock();

    const hasDefault = this.defaultValue !== null && this.defaultValue !== undefined && this.defaultValue !== '';
    if (hasDefault && !this.userEditing && this.defaultValue !== this.appliedLocationId) {
      this.originalValue = this.defaultValue;
      this.appliedLocationId = this.defaultValue;
      this.findLocationById(this.defaultValue);
    }

    if (!this.lockTouched) {
      if (this.disabled || !this.enabled) {
        this.form.get('location')!.disable();
      } else {
        this.form.get('location')!.enable();
      }
    }

    if (this.viewInitialized) {
      this.updateStylesByState();
    }
  }

  toggleEnabled() {
    if (!this.showBlocker) return;
    this.lockTouched = true;
    this.enabled = !this.enabled;
    if (!this.enabled) {
      this.form.get('location')?.disable();
    } else {
      this.form.get('location')?.enable();
    }
    this.updateStylesByState();
  }

  private applyInitialStyles() {
    this.updateStylesByState();
  }

  private safeApplyStyles(callback: () => void) {
    if (this.viewInitialized) {
      callback();
    }
  }

  private updateStylesByState() {
    if (!this.viewInitialized) return;

    const control = this.form.get('location');
    if (!this.enabled || this.disabled) {
      this.getStyleTextDisabled();
    } else if (control?.invalid || this.offTimeOut()) {
      this.getStyleError();
    } else {
      this.getStyleEnabled();
    }
  }

  startCountdown() {
    if (!this.enabled) return;
    this.clearCountdown();
    this.countdownTimer = setTimeout(() => {
      if (!this.form.valid && !this.hasWritten() && !this.form.get('location')?.value && this.enabled) {
        this.offTimeOut.set(true);
        this.updateStylesByState();
        this.cdr.detectChanges();
      }
    }, this.fieldErrorTimeout.delayMs(this.timeoutSeconds));
  }

  clearCountdown() {
    if (this.countdownTimer) {
      clearTimeout(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  resetErrorDebounce() {
    if (this.errorDebounceTimer) {
      clearTimeout(this.errorDebounceTimer);
    }

    this.errorDebounceTimer = setTimeout(() => {
      this.updateStylesByState();
    }, 1700);
  }

  getStyleError() {
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled');
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled');

        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.addClass(this.inbox.nativeElement, 'border-error');
      }
    });
  }

  getStyleEnabled() {
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled');
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error');

        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.addClass(this.inbox.nativeElement, 'border-enabled');
      }
    });
  }

  getStyleTextDisabled() {
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled');
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error');

        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.addClass(this.inbox.nativeElement, 'border-disabled');
      }
    });
  }

  private applyPreloadLock(): void {
    if (this.lockTouched || !this.showBlocker) return;
    const hasValue = this.defaultValue !== null && this.defaultValue !== undefined && this.defaultValue !== '';
    if (!hasValue) return;
    this.enabled = false;
    this.form.get('location')?.disable({ emitEvent: false });
    if (this.viewInitialized) {
      this.getStyleTextDisabled();
    }
  }

  findLocationById(id: any) {
    const request = ++this.locationRequest;
    this.listService.getUbicationById(id).subscribe({
      next: (response: any) => {
        if (request !== this.locationRequest || this.userEditing) return;
        const ubication = response.ubications;
        this.setLocationDefaultValue(ubication.name);
      }
    });
  }

  setLocationDefaultValue(defaultValue: string) {
    this.isUpdating = true;
    this.form.get('location')!.setValue(defaultValue);
    if (this.defaultValue) {
      this.isValid = true;
      this.clearCountdown();
      this.offTimeOut.set(false);
      this.updateStylesByState();
    }
    const locationObj = this.locations.find(location => location.id == this.defaultValue);
    if (locationObj && this.location) {
      this.location.nativeElement.value = locationObj.name;
      this.cdr.detectChanges();
    }
    setTimeout(() => {
      this.isUpdating = false;
    }, 100);
  }

  filterLocations(event: any) {
    if (!this.enabled) return;
    this.userEditing = true;
    this.locationRequest++;
    this.isValid = false;
    const query = String(event.target.value ?? '');
    if (!query.trim()) {
      this.valueState.emit({ value: null, isValid: false, isDirty: true });
    }
    const normalizedQuery = query.toLowerCase();
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
    this.isLoading = true;
    this.filterTimeout = setTimeout(() => {
      this.listService.getUbication(normalizedQuery)
        .subscribe({
          next: (response: any) => {
            this.locations = response.ubications;
            this.filteredLocations.set(
              this.locations.filter((location: any) => {
                const wordsArray = normalizedQuery.split(' ');
                const locationLower = location.name.toLowerCase();
                return wordsArray.every((word: any) => locationLower.includes(word));
              })
            );
            this.isLoading = false;
            this.cdr.detectChanges();
          },
          error: () => {
            this.isLoading = false;
          }
        });
    }, 1050);
  }

  selectLocation(idLocation: string | number, location: string) {
    if (!this.enabled) return;
    this.isUpdating = true;
    this.form.get('location')!.setValue(location);
    this.isValid = true;
    this.filteredLocations.set([]);

    const isDirty = idLocation !== this.originalValue;
    this.userEditing = false;
    this.appliedLocationId = idLocation;
    this.valueState.emit({ value: Number(idLocation), isValid: true, isDirty: isDirty });

    this.clearCountdown();
    this.offTimeOut.set(false);
    this.updateStylesByState();

    this.cdr.detectChanges();
    setTimeout(() => {
      this.isUpdating = false;
    }, 100);
  }

  esc() {
    if (!this.enabled) return;
    this.userEditing = true;
    this.locationRequest++;
    this.filteredLocations.set([]);
    this.form.get('location')!.setValue(null);
    this.isValid = false;
    this.valueState.emit({ value: null, isValid: false, isDirty: true });
    this.updateStylesByState();

    if (!this.defaultValue) {
      this.startCountdown();
    }
  }

  makeInvalid() {
    if (!this.enabled) return;
    this.userEditing = true;
    this.locationRequest++;
    this.isValid = false;
    this.valueState.emit({ value: null, isValid: false, isDirty: true });
    this.updateStylesByState();
  }

  validateBlur() {
    if (!this.enabled) return;
    setTimeout(() => {
      if (!this.isValid) {
        this.esc();
        this.cdr.detectChanges();
      }
    }, 1000);
  }

  hasError = () => {
    return (this.offTimeOut() || (this.form.get('location')?.invalid && this.form.get('location')?.touched)) && !this.isValid && !this.form.get('location')?.value && this.enabled;
  }

  ngOnDestroy() {
    if (this.errorDebounceTimer) {
      clearTimeout(this.errorDebounceTimer);
    }
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
    this.clearCountdown();
  }
}
